import { useState, useEffect, useRef } from "react";
import { Box, Typography, Paper, Button } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import colors from "../../Constants/colors";

export interface SortOption {
  id: string | number;
  label: string;
}

export interface SortControlsProps {
  sortBy: string | number;
  sortOrder: string | number;
  sortByOptions: SortOption[];
  sortOrderOptions: SortOption[];
  onSortByChange: (value: string | number) => void;
  onSortOrderChange: (value: string | number) => void;
}

export function SortControls({
  sortBy,
  sortOrder,
  sortByOptions,
  sortOrderOptions,
  onSortByChange,
  onSortOrderChange,
}: SortControlsProps) {
  const [openSortBy, setOpenSortBy] = useState(false);
  const [openSortOrder, setOpenSortOrder] = useState(false);
  const sortByRef = useRef<HTMLDivElement>(null);
  const sortOrderRef = useRef<HTMLDivElement>(null);

  const selectedSortByLabel =
    sortByOptions.find((opt) => opt.id === sortBy)?.label || "Sort by";
  const selectedSortOrderLabel =
    sortOrderOptions.find((opt) => opt.id === sortOrder)?.label || "Order";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sortByRef.current &&
        !sortByRef.current.contains(event.target as Node)
      ) {
        setOpenSortBy(false);
      }
      if (
        sortOrderRef.current &&
        !sortOrderRef.current.contains(event.target as Node)
      ) {
        setOpenSortOrder(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
      <Box
        sx={{ position: "relative", flex: 1, maxWidth: 200 }}
        ref={sortByRef}
      >
        <Button
          onClick={() => setOpenSortBy(!openSortBy)}
          fullWidth
          sx={{
            py: 0.5,
            px: 2,
            bgcolor: "white",
            border: `1px solid ${colors.gray300}`,
            borderRadius: 2,
            justifyContent: "space-between",
            textTransform: "none",
            color: colors.gray900,
            "&:hover": { bgcolor: colors.gray50 },
            "&:focus": {
              outline: "none",
              borderColor: colors.primary_dark,
            },
          }}
          endIcon={
            <KeyboardArrowDown
              sx={{
                transform: openSortBy ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          }
        >
          {selectedSortByLabel}
        </Button>

        {openSortBy && (
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              zIndex: 10,
              width: "100%",
              mt: 0.5,
              maxHeight: 240,
              overflow: "auto",
              borderRadius: 2,
              border: `1px solid ${colors.gray300}`,
            }}
          >
            {sortByOptions.map((option) => (
              <Box
                key={option.id}
                onClick={() => {
                  onSortByChange(option.id);
                  setOpenSortBy(false);
                }}
                sx={{
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  bgcolor:
                    sortBy === option.id ? `${colors.primary_dark}0F` : "white",
                  borderLeft:
                    sortBy === option.id
                      ? `4px solid ${colors.accent}`
                      : "4px solid transparent",
                  "&:hover": {
                    bgcolor: `${colors.primary_dark}0F`,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.gray900,
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      <Box
        sx={{ position: "relative", flex: 1, maxWidth: 200 }}
        ref={sortOrderRef}
      >
        <Button
          onClick={() => setOpenSortOrder(!openSortOrder)}
          fullWidth
          sx={{
            py: 0.5,
            px: 2,
            bgcolor: "white",
            border: `1px solid ${colors.gray300}`,
            borderRadius: 2,
            justifyContent: "space-between",
            textTransform: "none",
            color: colors.gray900,
            "&:hover": { bgcolor: colors.gray50 },
            "&:focus": {
              outline: "none",
              borderColor: colors.primary_dark,
            },
          }}
          endIcon={
            <KeyboardArrowDown
              sx={{
                transform: openSortOrder ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          }
        >
          {selectedSortOrderLabel}
        </Button>

        {openSortOrder && (
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              zIndex: 10,
              width: "100%",
              mt: 0.5,
              maxHeight: 240,
              overflow: "auto",
              borderRadius: 2,
              border: `1px solid ${colors.gray300}`,
            }}
          >
            {sortOrderOptions.map((option) => (
              <Box
                key={option.id}
                onClick={() => {
                  onSortOrderChange(option.id);
                  setOpenSortOrder(false);
                }}
                sx={{
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  bgcolor:
                    sortOrder === option.id
                      ? `${colors.primary_dark}0F`
                      : "white",
                  borderLeft:
                    sortOrder === option.id
                      ? `4px solid ${colors.accent}`
                      : "4px solid transparent",
                  "&:hover": {
                    bgcolor: `${colors.primary_dark}0F`,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.gray900,
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
